import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { PlugZap, FlaskConical, Trash2, Activity, CheckCircle2, XCircle } from "lucide-react";
import { Panel, Chip, Banner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmt } from "@/lib/api";
import { LiveOrderTest } from "@/components/settings/LiveOrderTest";
import { BrokerGuide } from "@/components/settings/BrokerGuide";


export function BrokerTab() {
  const [b, setB] = useState(null);
  const [f, setF] = useState({ metaapi_token: "", login: "", password: "", server: "", environment: "demo", region: "london", label: "MT5" });
  const [test, setTest] = useState(null);
  const [conn, setConn] = useState(null);
  const [testing, setTesting] = useState(false);
  const dirty = () => !!(f.metaapi_token || f.login || f.password || (b && f.server && f.server !== b.server));
  const persist = async () => {
    if (!dirty()) return;
    const body = { ...f };
    ["metaapi_token", "login", "password"].forEach((k) => { if (!body[k]) delete body[k]; });
    await api.put("/broker", body);
    setF((x) => ({ ...x, metaapi_token: "", password: "" }));
    toast.success("MT5 details saved and encrypted");
  };
  const testConnection = async () => {
    setTesting(true);
    setConn(null);
    try { await persist(); const { data } = await api.post("/broker/test-connection"); setConn(data); load(); } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };
  const load = useCallback(async () => {
    const { data } = await api.get("/broker/status");
    setB(data);
    setF((x) => ({ ...x, server: x.server || data.server || "", environment: data.environment || "demo", region: data.region || x.region }));
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);
  const run = async (fn, msg) => { try { const r = await fn(); if (msg) toast.success(msg); load(); return r; } catch (e) { toast.error(errMsg(e)); return null; } };
  const save = () => run(async () => {
    const body = { ...f };
    ["metaapi_token", "login", "password"].forEach((k) => { if (!body[k]) delete body[k]; });
    await api.put("/broker", body);
    setConn(null);
    setTest(null);
    setF((x) => ({ ...x, metaapi_token: "", password: "" }));
  }, "MT5 details saved and encrypted");
  if (!b) return null;
  const field = (k, label, type = "text", ph = "") => (
    <label className="space-y-1 text-xs text-slate-400" key={k}>{label}
      <Input data-testid={`broker-${k}-input`} type={type} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-10 bg-slate-950" />
    </label>
  );
  return (
    <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr] [&>*]:min-w-0">
      <Panel title="MT5 account" testid="settings-broker" right={<Chip status={b.status === "connected" ? "ON" : b.configured ? "WARNING" : "OFF"} label={b.connecting ? "connecting" : b.status.replaceAll("_", " ")} testid="broker-connection-chip" />}>
        {b.configured && (
          <div className="mb-4 num text-xs text-slate-400" data-testid="broker-configured-summary">
            Configured: login {b.login_masked}, server {b.server}, {b.environment} account{b.account_id ? `, MetaApi id ${b.account_id.slice(0, 8)}...` : ""}{b.verified ? ", verified" : ", not verified yet"}
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          {field("metaapi_token", "MetaApi API token", "password", b.configured ? "Saved. Enter to replace" : "")}
          {field("login", "MT5 login number", "text", b.configured ? `Saved (${b.login_masked})` : "e.g. 51234567")}
          {field("password", "MT5 trading password", "password", b.configured ? "Saved. Enter to replace" : "")}
          {field("server", "MT5 server", "text", "e.g. ICMarketsSC-Demo")}
          <label className="space-y-1 text-xs text-slate-400">Account type
            <select data-testid="broker-environment-select" value={f.environment} onChange={(e) => setF({ ...f, environment: e.target.value })} className="h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-200"><option value="demo">Demo</option><option value="live">Live</option></select>
          </label>
          <label className="space-y-1 text-xs text-slate-400">MetaApi region
            <select data-testid="broker-region-select" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className="h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-200">{["london", "new-york", "singapore"].map((r) => <option key={r} value={r}>{r}</option>)}</select>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="broker-save-button" className="btn-gold" onClick={save}>Save</Button>
          <Button data-testid="broker-connect-button" variant="secondary" disabled={!b.configured || b.connecting} onClick={() => run(async () => { await persist(); return api.post("/broker/connect"); }, "Connecting to MT5")}><PlugZap className="mr-1.5 h-4 w-4" />Connect</Button>
          <Button data-testid="broker-test-connection-button" variant="secondary" disabled={!b.configured || testing} onClick={testConnection}><Activity className="mr-1.5 h-4 w-4" />{testing ? "Testing" : "Test connection"}</Button>
          <Button data-testid="broker-test-button" variant="secondary" disabled={!b.account_id} onClick={async () => { const r = await run(() => api.post("/broker/test")); if (r) setTest(r.data); }}><FlaskConical className="mr-1.5 h-4 w-4" />Test login</Button>
          {b.configured && <Button data-testid="broker-remove-button" variant="ghost" className="text-rose-300" onClick={() => window.confirm("Remove the saved MT5 account from Trenex Systems?") && run(async () => { await api.delete("/broker"); setConn(null); setTest(null); }, "Removed")}><Trash2 className="mr-1.5 h-4 w-4" />Remove</Button>}
        </div>
        {b.error && <div className="mt-4"><Banner tone={b.status === "error" ? "error" : "info"} testid="broker-error">{b.error}</Banner></div>}
        {conn && (
          <div className="mt-4 space-y-2 rounded-lg border border-white/[0.06] bg-[#0A0E16]/80 p-4" data-testid="broker-test-connection-result">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Chip status={conn.ok ? "ON" : "TRIGGERED"} label={conn.ok ? "All checks passed" : "Check failed"} testid="broker-test-connection-status" />
            </div>
            {conn.steps.map((s, i) => (
              <div key={s.name} className="flex gap-2 text-sm" data-testid={`broker-test-step-${i}`}>
                {s.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />}
                <div><div className="text-slate-200">{s.name}</div><div className="text-xs text-slate-400">{s.detail}</div></div>
              </div>
            ))}
          </div>
        )}
        {test && <div className="mt-4"><Banner tone="ok" testid="broker-test-result">Logged in: {test.name}, balance {fmt(test.balance)} {test.currency}, server {test.server}</Banner></div>}
        <p className="mt-4 text-xs text-slate-500">Your token and password are stored encrypted and are never sent back to the browser.</p>
      </Panel>
      <BrokerGuide />
      {b.account_id && <LiveOrderTest connected={b.status === "connected"} />}
    </div>
  );
}
